import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink } from "components";
import { useAnchor } from "hooks";
import { TickIcon } from "icons/TickIcon";
import { Features6BContent } from "types/graphql/generated";

interface Props {
  content: Features6BContent;
}

const Features6B: VFC<Props> = withErrorBoundary(
  ({ content: { features6bTitle, features6bDescription, features6bLink, features6bLinkLabel, features6bList } }) => {
    useAnchor("features6b");

    return (
      <StyledFeatures6A className="features6b Content">
        <div className="Features6b">
          <div>
            <Markdown className="Features6b__title Font_48_54_600 lg:Font_40_48_600 sm:Font_32_40_600">
              {features6bTitle}
            </Markdown>
            <Markdown className="Features6b__description Color_secondary Font_24_32_450 sm:Font_18_26_450">
              {features6bDescription}
            </Markdown>
            {features6bLink && (
              <ArrowLink href={features6bLink} className="Features6b__link Font_18_26_600">
                {features6bLinkLabel}
              </ArrowLink>
            )}
          </div>
          <ul className="Features6b__list">
            {features6bList.map(({ id, features6bListItem }) => (
              <li key={id} className="Features6b__listItem">
                <TickIcon className="Features6b__icon" />
                <Markdown className="Features6b__text Font_18_26_600">{features6bListItem}</Markdown>
              </li>
            ))}
          </ul>
        </div>
      </StyledFeatures6A>
    );
  },
  {}
);

const StyledFeatures6A = styled.section`
  padding-top: 64px;
  margin-top: -64px;

  .Features6b {
    display: flex;
    align-items: center;
  }

  .Features6b__title {
    max-width: 490px;
  }

  .Features6b__description {
    margin-top: 24px;
    max-width: 662px;

    a {
      text-decoration: dotted;
    }
  }

  .Features6b__link {
    margin-top: 24px;
  }

  .Features6b__list {
    list-style: none;
    margin-left: 80px;
    min-width: 380px;
  }

  .Features6b__listItem {
    background: ${({ theme }) => theme.colors.fog["10"]};
    border-radius: 32px;
    padding: 13px 16px;
    display: flex;
    align-items: center;
    width: fit-content;

    &:not(:first-child) {
      margin-top: 24px;
    }
  }

  .Features6b__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .Features6b__text {
    margin-left: 12px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Features6b__title {
      max-width: unset;
    }

    .Features6b {
      flex-direction: column;
      align-items: flex-start;
    }

    .Features6b__list {
      margin-left: 0;
      margin-top: 64px;
      min-width: unset;
    }
  }
`;

gql`
  fragment Features6BContent on ComponentFeaturesFeatures6B {
    features6bTitle
    features6bDescription
    features6bLink
    features6bLinkLabel
    features6bList {
      id
      features6bListItem
    }
  }
`;

export { Features6B };
export type { Props as Features6bProps };
